import _ from '@lodash';

import AspectEditService from 'app/admin/aspect/service/AspectEditService';
import MeasurementEditService from 'app/admin/measurement/service/MeasurementEditService';
import ModelService from 'app/admin/model/service/ModelService';
import OptimizationCaseEditService from 'app/admin/optimizationCase/service/OptimizationCaseEditService';

class StorageTaskManagerService {
	constructor() {
		this.aspectNamesMap = {};
		this.measurement = null;
		this.measurementMap = {};
		this.measurementNamesMap = {};

		this.aspectDataMap = {};
		this.measurementDataMap = {};
		this.modelDataMap = {};
		this.modelValidationToleranceNamesMap = {};

		this.optimizationCaseAspectMap = {};
	}

	_extractId(val) {
		if (val === null || val === undefined) return null;
		if (typeof val === 'string' || typeof val === 'number') return String(val);
		if (typeof val === 'object') {
			if (val.id !== undefined && val.id !== null) return String(val.id);
			if (val.value !== undefined && val.value !== null) return String(val.value);
		}
		return null;
	}

	_getAspectIdFromOptimizationCaseResponse(resp) {
		const candidates = [
			resp?.aspect,
			resp?.aspectId,
			resp?.data?.aspect,
			resp?.data?.aspectId,
			resp?.optimizationCase?.aspect,
			resp?.optimizationCase?.aspectId,
		];
		for (const c of candidates) {
			const id = this._extractId(c);
			if (id) return id;
		}
		return null;
	}

	_getAttrList(src, snakeKey, camelKey) {
		if (!src) return [];
		const value = src[camelKey] != null ? src[camelKey] : src[snakeKey];
		return Array.isArray(value) ? value : [];
	}

	_getModelAttrList(modelData, snakeKey, camelKey) {
		const direct = this._getAttrList(modelData, snakeKey, camelKey);
		if (direct.length > 0) return direct;
		return this._getAttrList(modelData?.export, snakeKey, camelKey);
	}

	_isObject(value) {
		return !!value && typeof value === 'object' && !Array.isArray(value);
	}

	_extractChildNames(source) {
		const seen = new Set();
		const values = [];

		const push = name => {
			const n = String(name || '').trim();
			if (!n || seen.has(n)) return;
			seen.add(n);
			values.push(n);
		};

		if (source == null) return values;

		if (Array.isArray(source)) {
			for (let i = 0; i < source.length; i += 1) {
				const item = source[i];
				if (item == null) continue;

				if (typeof item === 'string' || typeof item === 'number') {
					push(item);
					continue;
				}

				if (this._isObject(item)) {
					if (item.name != null) {
						push(item.name);
						continue;
					}
					if (item.value != null && !this._isObject(item.value) && !Array.isArray(item.value)) {
						push(item.value);
						continue;
					}
					if (item.key != null) {
						push(item.key);
						continue;
					}
					Object.keys(item).forEach(push);
				}
			}
			return values;
		}

		if (this._isObject(source)) {
			Object.keys(source).forEach(push);
		}

		return values;
	}

	_isComponentAttribute(attr, modelAttr) {
		const attrType = String(attr?.type || '').trim();
		const modelType = String(modelAttr?.type || '').trim();
		return attrType.indexOf('Component') === 0 || modelType.indexOf('Component') === 0;
	}

	_findModelAttribute(primaryList, fallbackList, names) {
		const requestedNames = (Array.isArray(names) ? names : [names])
			.map(name => String(name || '').trim())
			.filter(Boolean);

		const findByName = (list, name) => {
			const arr = Array.isArray(list) ? list : [];
			for (let i = 0; i < arr.length; i += 1) {
				if (String(arr[i]?.name || '').trim() === name) return arr[i];
			}
			return null;
		};

		for (let i = 0; i < requestedNames.length; i += 1) {
			const hit = findByName(primaryList, requestedNames[i]);
			if (hit) return hit;
		}

		for (let i = 0; i < requestedNames.length; i += 1) {
			const hit = findByName(fallbackList, requestedNames[i]);
			if (hit) return hit;
		}

		return null;
	}

	_buildMeasurementInfo(measurementData) {
		const info = {
			exactNamesByCategory: {
				input: new Set(),
				output: new Set(),
				all: new Set()
			},
			rootMap: new Map()
		};

		const getRootEntry = rootName => {
			if (!info.rootMap.has(rootName)) {
				info.rootMap.set(rootName, {
					children: new Set(),
					categories: new Set(),
					field: null
				});
			}
			return info.rootMap.get(rootName);
		};

		const fields = Array.isArray(measurementData?.fields) ? measurementData.fields : [];
		for (let i = 0; i < fields.length; i += 1) {
			const field = fields[i];
			const rawName = String(field?.name || '').trim();
			if (!rawName) continue;

			const rawCategory = String(field?.category || '').trim().toLowerCase();
			const category = rawCategory === 'input' || rawCategory === 'output' ? rawCategory : 'all';

			info.exactNamesByCategory.all.add(rawName);
			info.exactNamesByCategory[category].add(rawName);

			const nameParts = rawName.split('.');
			const rootName = nameParts[0];
			const childName = nameParts.length > 1 ? nameParts.slice(1).join('.') : null;
			const rootEntry = getRootEntry(rootName);
			if (!rootEntry.field) rootEntry.field = field;
			rootEntry.categories.add(category);

			if (childName) rootEntry.children.add(childName);

			const children = this._extractChildNames(field?.components);
			for (let j = 0; j < children.length; j += 1) {
				const child = children[j];
				rootEntry.children.add(child);
				info.exactNamesByCategory.all.add(rootName + '.' + child);
				info.exactNamesByCategory[category].add(rootName + '.' + child);
			}
		}

		return info;
	}

	_measurementHasName(measurementInfo, name, preferredCategory) {
		const n = String(name || '').trim();
		if (!n) return false;
		if (preferredCategory && measurementInfo?.exactNamesByCategory?.[preferredCategory]?.has(n)) return true;
		return measurementInfo?.exactNamesByCategory?.all?.has(n) === true;
	}

	_getMeasurementRootEntry(measurementInfo, names, preferredCategory) {
		const requestedNames = (Array.isArray(names) ? names : [names])
			.map(name => String(name || '').trim())
			.filter(Boolean);

		for (let i = 0; i < requestedNames.length; i += 1) {
			const entry = measurementInfo?.rootMap?.get(requestedNames[i]);
			if (entry && (!preferredCategory || entry.categories.has(preferredCategory))) return entry;
		}

		for (let i = 0; i < requestedNames.length; i += 1) {
			const entry = measurementInfo?.rootMap?.get(requestedNames[i]);
			if (entry) return entry;
		}

		return null;
	}

	_getComponentChildren(attr, modelAttr, measurementRootEntry) {
		const seen = new Set();
		const values = [];
		const push = name => {
			const n = String(name || '').trim();
			if (!n || seen.has(n)) return;
			seen.add(n);
			values.push(n);
		};

		const attrChildren = this._extractChildNames(attr?.value);
		const modelChildren = this._extractChildNames(modelAttr?.value);
		const measurementChildren = measurementRootEntry ? Array.from(measurementRootEntry.children) : [];

		for (let i = 0; i < attrChildren.length; i += 1) push(attrChildren[i]);
		for (let i = 0; i < modelChildren.length; i += 1) push(modelChildren[i]);

		if (values.length === 0) {
			for (let i = 0; i < measurementChildren.length; i += 1) push(measurementChildren[i]);
		}

		return values;
	}

	async _getAspectData(aspectId) {
		const id = this._extractId(aspectId);
		if (!id) return null;
		if (!this.aspectDataMap[id]) {
			this.aspectDataMap[id] = AspectEditService.getData(id).catch(error => {
				delete this.aspectDataMap[id];
				throw error;
			});
		}
		return this.aspectDataMap[id];
	}

	async _getMeasurementData(measurementId) {
		const id = this._extractId(measurementId);
		if (!id) return null;
		if (!this.measurementDataMap[id]) {
			this.measurementDataMap[id] = MeasurementEditService.getData(id).then(resp => {
				this.measurement = id;
				this.measurementMap[id] = Array.isArray(resp?.fields) ? resp.fields : [];
				return resp;
			}).catch(error => {
				delete this.measurementDataMap[id];
				throw error;
			});
		}
		return this.measurementDataMap[id];
	}

	async _getModelData(modelId) {
		const id = this._extractId(modelId);
		if (!id) return null;
		if (!this.modelDataMap[id]) {
			this.modelDataMap[id] = ModelService.getData(id).catch(error => {
				delete this.modelDataMap[id];
				throw error;
			});
		}
		return this.modelDataMap[id];
	}

	async getAspectIdForOptimizationCase(optimizationCaseId) {
		const ocId = this._extractId(optimizationCaseId);
		if (!ocId) return null;

		if (this.optimizationCaseAspectMap[ocId]) {
			return this.optimizationCaseAspectMap[ocId];
		}
		try {
			const resp = await OptimizationCaseEditService.getData(ocId);
			const aspectId = this._getAspectIdFromOptimizationCaseResponse(resp);
			if (aspectId) this.optimizationCaseAspectMap[ocId] = aspectId;
			return aspectId || null;
		} catch {
			return null;
		}
	}

	getNamesForAspect(v, f, aspectId, mode = 'inputs') {
		const id = aspectId || v?.aspect;
		const key = (id || 'none') + '::' + mode;

		return new Promise((resolve, reject) => {
			if (!id) return resolve([]);

			if (this.aspectNamesMap[key]) {
				return resolve(this.aspectNamesMap[key]);
			}

			AspectEditService.getData(id).then(resp => {
				const inputs = Array.isArray(resp?.inputAttributes) ? resp.inputAttributes : [];
				const outputs = Array.isArray(resp?.outputAttributes) ? resp.outputAttributes : [];

				let source = [];
				if (mode === 'inputs') source = inputs;
				else if (mode === 'outputs') source = outputs;
				else if (mode === 'both') source = inputs.concat(outputs);

				const seen = new Set();
				const values = [];

				const push = name => {
					const n = String(name || '').trim();
					if (!n || seen.has(n)) return;
					seen.add(n);
					values.push({ label: n, value: n });
				};

				for (let i = 0; i < source.length; i += 1) {
					const item = source[i];
					if (!item?.name) continue;

					const main = String(item.name).trim();
					push(main);

					const childNames = this._extractChildNames(item?.value);
					for (let j = 0; j < childNames.length; j += 1) {
						push(main + '.' + childNames[j]);
					}
				}

				this.aspectNamesMap[key] = values;
				resolve(values);
			}).catch(error => reject(error));
		});
	}

	async getNamesForModelValidationTolerance(v, f, mode = 'both') {
		const aspectId = this._extractId(v?.aspect);
		const measurementId = this._extractId(v?.measurement);
		const key = (aspectId || 'none') + '::' + (measurementId || 'none') + '::' + mode;

		if (!aspectId || !measurementId) return [];
		if (this.modelValidationToleranceNamesMap[key]) return this.modelValidationToleranceNamesMap[key];

		try {
			const aspectData = await this._getAspectData(aspectId);
			const measurementData = await this._getMeasurementData(measurementId);
			const modelData = aspectData?.model ? await this._getModelData(aspectData.model) : null;

			const aspectInputs = this._getAttrList(aspectData, 'input_attributes', 'inputAttributes');
			const aspectOutputs = this._getAttrList(aspectData, 'output_attributes', 'outputAttributes');
			const modelInputs = this._getModelAttrList(modelData, 'input_attributes', 'inputAttributes');
			const modelOutputs = this._getModelAttrList(modelData, 'output_attributes', 'outputAttributes');
			const measurementInfo = this._buildMeasurementInfo(measurementData);

			let aspectEntries = [];
			if (mode === 'inputs') aspectEntries = aspectInputs.map(attr => ({ attr, io: 'input' }));
			else if (mode === 'outputs') aspectEntries = aspectOutputs.map(attr => ({ attr, io: 'output' }));
			else aspectEntries = aspectInputs.map(attr => ({ attr, io: 'input' })).concat(aspectOutputs.map(attr => ({ attr, io: 'output' })));

			const seen = new Set();
			const values = [];
			const push = name => {
				const n = String(name || '').trim();
				if (!n || seen.has(n)) return;
				seen.add(n);
				values.push({ label: n, value: n });
			};

			for (let i = 0; i < aspectEntries.length; i += 1) {
				const entry = aspectEntries[i];
				const attr = entry?.attr;
				const aspectName = String(attr?.name || '').trim();
				if (!aspectName) continue;

				const mappedModelName = String(attr?.modelFieldName ?? attr?.model_field_name ?? '').trim();
				const primaryModelList = entry.io === 'output' ? modelOutputs : modelInputs;
				const fallbackModelList = entry.io === 'output' ? modelInputs : modelOutputs;
				const preferredCategory = entry.io === 'output' ? 'output' : 'input';

				const modelAttr = this._findModelAttribute(primaryModelList, fallbackModelList, [mappedModelName, aspectName]);
				const measurementRootEntry = this._getMeasurementRootEntry(measurementInfo, [aspectName, mappedModelName], preferredCategory);
				const isComponent = this._isComponentAttribute(attr, modelAttr);

				if (isComponent) {
					const measurementHasRoot =
						this._measurementHasName(measurementInfo, aspectName, preferredCategory)
						|| this._measurementHasName(measurementInfo, mappedModelName, preferredCategory)
						|| measurementRootEntry != null;

					if (!measurementHasRoot) continue;

					const children = this._getComponentChildren(attr, modelAttr, measurementRootEntry);
					if (children.length === 0) continue;

					const measurementChildren = measurementRootEntry ? measurementRootEntry.children : null;
					const restrictToMeasurementChildren = measurementChildren && measurementChildren.size > 0;

					for (let j = 0; j < children.length; j += 1) {
						const child = children[j];
						if (restrictToMeasurementChildren) {
							const hasChild =
								measurementChildren.has(child)
								|| this._measurementHasName(measurementInfo, aspectName + '.' + child, preferredCategory)
								|| (mappedModelName && this._measurementHasName(measurementInfo, mappedModelName + '.' + child, preferredCategory));
							if (!hasChild) continue;
						}
						push(aspectName + '.' + child);
					}
					continue;
				}

				const hasScalarMeasurement =
					this._measurementHasName(measurementInfo, aspectName, preferredCategory)
					|| this._measurementHasName(measurementInfo, mappedModelName, preferredCategory);

				if (hasScalarMeasurement) {
					push(aspectName);
				}
			}

			this.modelValidationToleranceNamesMap[key] = values;
			return values;
		} catch (e) {
			console.log(e);
			return [];
		}
	}

	getNamesForMeasurement(v, f) {
		return new Promise((resolve, reject) => {
			if (v.measurement && !this.measurementNamesMap[v.measurement]) {
				MeasurementEditService.getData(v.measurement).then(resp => {
					this.measurement = v.measurement;
					this.measurementMap[v.measurement] = resp.fields;

					let values = [];
					for (let i = 0; i < resp.fields.length; i += 1) {
						const option = { label: resp.fields[i].name, value: resp.fields[i].name };
						values.push(option);
					}
					this.measurementNamesMap[v.measurement] = values;
					resolve(values);
				}).catch(e => reject(e));
			} else if (v.measurement && this.measurementNamesMap[v.measurement]) {
				resolve(this.measurementNamesMap[v.measurement]);
			} else {
				resolve([]);
			}
		});
	}

	isMeasurementNameComponent(name) {
		if (name && this.measurement) {
			for (let i = 0; i < this.measurementMap[this.measurement].length; i += 1) {
				let item = this.measurementMap[this.measurement][i];
				if (item.name === name && item.type === 'Dict')
					return true;
			}
			return false;
		}
		return false;
	}

	getNamesForOptimizationCaseAspect(v, f, mode = 'inputs') {
		return new Promise(async (resolve, reject) => {
			try {
				const ocId = this._extractId(v?.optimizationCase);
				if (!ocId) {
					if (v?.measurement && this.measurementNamesMap[v.measurement]) {
						return resolve(this.measurementNamesMap[v.measurement]);
					}
					return resolve([]);
				}

				const aspectId = await this.getAspectIdForOptimizationCase(ocId);
				if (!aspectId) {
					return resolve([]);
				}

				const values = await this.getNamesForAspect(v, f, aspectId, mode);
				resolve(values);
			} catch (e) {
				reject(e);
			}
		});
	}

	async getTypeForAspectInput(name, aspectId, mode = 'inputs') {
		if (!name || !aspectId) return null;
		try {
			const resp = await this._getAspectData(aspectId);
			const inputs = Array.isArray(resp?.inputAttributes) ? resp.inputAttributes : [];
			const outputs = Array.isArray(resp?.outputAttributes) ? resp.outputAttributes : [];

			let pool = inputs;
			if (mode === 'outputs') pool = outputs;
			if (mode === 'both') pool = inputs.concat(outputs);

			const fullName = String(name || '').trim();
			const rootName = fullName.split('.')[0];
			const hit = pool.find(item => item?.name === fullName || item?.name === rootName);
			return hit ? (hit.type ?? null) : null;
		} catch {
			return null;
		}
	}
}

export default StorageTaskManagerService;